import React, { useEffect, useRef, useState } from 'react';
import Input from '../Input/Input';
import './Search.css';

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function Search({
  value: controlledValue,
  defaultValue = '',
  onChange,
  onSearch,
  debounceMs = 0,
  placeholder = 'Search...',
  size = 'md',
  showClear = true,
  ...rest
}) {
  const isControlled = controlledValue !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const value = isControlled ? controlledValue : internal;

  const debounceRef = useRef(null);
  const lastSearched = useRef(defaultValue);

  useEffect(() => {
    if (!onSearch) return;
    if (debounceMs <= 0) {
      if (value !== lastSearched.current) {
        lastSearched.current = value;
        onSearch(value);
      }
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value !== lastSearched.current) {
        lastSearched.current = value;
        onSearch(value);
      }
    }, debounceMs);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, debounceMs, onSearch]);

  const handleChange = (e) => {
    if (!isControlled) setInternal(e.target.value);
    if (onChange) onChange(e);
  };

  const handleClear = () => {
    if (!isControlled) setInternal('');
    if (onChange) onChange({ target: { value: '' } });
  };

  const suffix = showClear && value ? (
    <button
      type="button"
      className="ui-search__clear"
      onClick={handleClear}
      aria-label="Clear search"
    >
      <ClearIcon />
    </button>
  ) : null;

  return (
    <Input
      type="search"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      size={size}
      prefix={<span className="ui-search__icon"><SearchIcon /></span>}
      suffix={suffix}
      className="ui-search"
      {...rest}
    />
  );
}

export default Search;
